import { type InfiniteData, type QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BOARD_DONE_DAYS,
  type BoardDTO,
  type DoneHistoryPage,
  type LabelDTO,
  type LabelEventDTO,
  type MeDTO,
  type PersonalListDTO,
  type ReminderDTO,
  type ReminderInput,
  type SavedFilterDTO,
  type TaskDetailDTO,
  type TaskDTO,
  type TaskFilter,
  MAX_TASK_REFS,
  type TaskRefDTO,
} from "@justone/shared";
import { http } from "./http";
import { keys as baseKeys } from "./queries";
import type { BootstrapDTO } from "@justone/shared";

export const taskKeys = {
  board: (channelId: number) => ["board", channelId] as const,
  boardDone: (channelId: number) => ["board-done", channelId] as const,
  task: (id: number) => ["task", id] as const,
  /** `#T-123` 칩 (볼 수 없으면 null) */
  ref: (id: number) => ["task-ref", id] as const,
  search: (filter: TaskFilter & { includeDone?: boolean }) => ["tasks", filter] as const,
  labels: ["labels"] as const,
  labelEvents: ["label-events"] as const,
  lists: ["lists"] as const,
  filters: ["filters"] as const,
};

/* ───────────── 조회 ───────────── */

export const useBoard = (channelId: number) =>
  useQuery({ queryKey: taskKeys.board(channelId), queryFn: () => http.get<BoardDTO>(`/api/channels/${channelId}/tasks`), staleTime: Infinity });

export function useDoneHistory(channelId: number, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: taskKeys.boardDone(channelId),
    queryFn: ({ pageParam }) => http.get<DoneHistoryPage>(`/api/channels/${channelId}/tasks/done?offset=${pageParam}`),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (last.hasMore ? pages.reduce((n, p) => n + p.tasks.length, 0) : undefined),
    enabled,
  });
}

export const useTask = (id: number | null) =>
  useQuery({ queryKey: taskKeys.task(id ?? 0), queryFn: () => http.get<TaskDetailDTO>(`/api/tasks/${id}`), enabled: id !== null, staleTime: 10_000 });

/* 할일 칩: 한 화면에 칩이 여럿이어도 같은 틱의 요청을 묶어 한 번에 묻는다 */
let refBatch: Map<number, { resolve: (t: TaskRefDTO | null) => void; reject: (e: unknown) => void }[]> | null = null;

function fetchTaskRef(id: number): Promise<TaskRefDTO | null> {
  return new Promise((resolve, reject) => {
    if (!refBatch) {
      const batch = (refBatch = new Map());
      setTimeout(() => {
        refBatch = null;
        const ids = [...batch.keys()];
        for (let i = 0; i < ids.length; i += MAX_TASK_REFS) {
          const chunk = ids.slice(i, i + MAX_TASK_REFS);
          http
            .get<TaskRefDTO[]>(`/api/tasks/refs?ids=${chunk.join(",")}`)
            .then((rows) => {
              for (const tid of chunk) for (const w of batch.get(tid) ?? []) w.resolve(rows.find((r) => r.id === tid) ?? null);
            })
            .catch((e) => {
              for (const tid of chunk) for (const w of batch.get(tid) ?? []) w.reject(e);
            });
        }
      }, 10);
    }
    refBatch.set(id, [...(refBatch.get(id) ?? []), { resolve, reject }]);
  });
}

export const taskRefQuery = (id: number) => ({ queryKey: taskKeys.ref(id), queryFn: () => fetchTaskRef(id), staleTime: Infinity });
export const useTaskRef = (id: number) => useQuery(taskRefQuery(id));

export const toTaskRef = (t: TaskDTO): TaskRefDTO => ({
  id: t.id,
  channelId: t.channelId,
  title: t.title,
  status: t.status,
  assigneeId: t.assigneeId,
  dueDate: t.dueDate,
  dueTime: t.dueTime,
  priority: t.priority,
  rrule: t.rrule,
  repeatFromCompletion: t.repeatFromCompletion,
  subtaskCount: t.subtaskCount,
  subtaskDone: t.subtaskDone,
  deleted: t.deleted,
});

export const useTaskSearch = (filter: TaskFilter & { includeDone?: boolean }, enabled = true) =>
  useQuery({ queryKey: taskKeys.search(filter), queryFn: () => http.post<TaskDTO[]>("/api/tasks/search", filter), enabled, staleTime: 30_000 });

export const useLabels = () => useQuery({ queryKey: taskKeys.labels, queryFn: () => http.get<LabelDTO[]>("/api/labels"), staleTime: Infinity });
export const useLabelEvents = () => useQuery({ queryKey: taskKeys.labelEvents, queryFn: () => http.get<LabelEventDTO[]>("/api/labels/events") });
export const useLists = () => useQuery({ queryKey: taskKeys.lists, queryFn: () => http.get<PersonalListDTO[]>("/api/lists"), staleTime: Infinity });
export const useFilters = () => useQuery({ queryKey: taskKeys.filters, queryFn: () => http.get<SavedFilterDTO[]>("/api/filters"), staleTime: Infinity });

/* ───────────── 캐시 (응답과 실시간 이벤트가 같이 쓴다) ───────────── */

const DAY_MS = 86_400_000;

/** 보드에 둘 할일인지: 최상위가 아니어도 둔다 (진행률·필터용). 오래전에 끝낸 것은 뺀다. */
function onBoard(t: TaskDTO): boolean {
  if (t.deleted || t.channelId === null) return false;
  return t.status !== "done" || !t.completedAt || Date.now() - new Date(t.completedAt).getTime() < BOARD_DONE_DAYS * DAY_MS;
}

export function upsertTask(qc: QueryClient, task: TaskDTO) {
  // 다른 채널 보드에 있던 할일이면 (개인 → 팀 이동 등) 거기서 뺀다
  for (const [key, board] of qc.getQueriesData<BoardDTO>({ queryKey: ["board"] })) {
    const channelId = key[1] as number;
    if (!board) continue;
    const has = board.tasks.some((t) => t.id === task.id);
    if (channelId === task.channelId && onBoard(task)) {
      qc.setQueryData<BoardDTO>(key, { tasks: has ? board.tasks.map((t) => (t.id === task.id ? task : t)) : [...board.tasks, task] });
    } else if (has) {
      qc.setQueryData<BoardDTO>(key, { tasks: board.tasks.filter((t) => t.id !== task.id) });
    }
  }
  qc.setQueryData<TaskDetailDTO>(taskKeys.task(task.id), (d) => (d ? { ...d, task } : d));
  qc.setQueriesData<TaskDetailDTO>({ queryKey: ["task"] }, (d) => (d && d.subtasks.some((s) => s.id === task.id) ? { ...d, subtasks: d.subtasks.map((s) => (s.id === task.id ? task : s)) } : d));
  // 칩은 이미 그린 것만 고친다 (이벤트마다 캐시를 늘리지 않게)
  if (qc.getQueryState(taskKeys.ref(task.id))) qc.setQueryData(taskKeys.ref(task.id), toTaskRef(task));
  if (task.parentId !== null) void qc.invalidateQueries({ queryKey: taskKeys.task(task.parentId) });
  void qc.invalidateQueries({ queryKey: ["tasks"] });
  if (task.channelId === null) void qc.invalidateQueries({ queryKey: taskKeys.lists });
}

export function removeTask(qc: QueryClient, id: number, parentId: number | null) {
  for (const [key, board] of qc.getQueriesData<BoardDTO>({ queryKey: ["board"] })) {
    if (board?.tasks.some((t) => t.id === id)) qc.setQueryData<BoardDTO>(key, { tasks: board.tasks.filter((t) => t.id !== id) });
  }
  qc.setQueriesData<TaskDTO[]>({ queryKey: ["tasks"] }, (list) => list?.filter((t) => t.id !== id));
  qc.setQueryData<TaskDetailDTO>(taskKeys.task(id), (d) => (d ? { ...d, task: { ...d.task, deleted: true } } : d));
  qc.setQueryData<TaskRefDTO | null>(taskKeys.ref(id), (r) => (r ? { ...r, deleted: true } : r));
  if (parentId !== null) void qc.invalidateQueries({ queryKey: taskKeys.task(parentId) });
  void qc.invalidateQueries({ queryKey: ["tasks"] });
  void qc.invalidateQueries({ queryKey: taskKeys.lists });
}

/** 응답 전에 화면부터 바꾼다 (체크, 끌어 옮기기) */
function patchTaskLocal(qc: QueryClient, id: number, patch: Partial<TaskDTO>) {
  const apply = (t: TaskDTO) => (t.id === id ? { ...t, ...patch } : t);
  qc.setQueriesData<BoardDTO>({ queryKey: ["board"] }, (b) => (b ? { tasks: b.tasks.map(apply) } : b));
  qc.setQueriesData<TaskDTO[]>({ queryKey: ["tasks"] }, (list) => list?.map(apply));
  qc.setQueryData<TaskDetailDTO>(taskKeys.task(id), (d) => (d ? { ...d, task: apply(d.task) } : d));
  qc.setQueriesData<TaskDetailDTO>({ queryKey: ["task"] }, (d) => (d && d.subtasks.some((s) => s.id === id) ? { ...d, subtasks: d.subtasks.map(apply) } : d));
  qc.setQueryData<TaskRefDTO | null>(taskKeys.ref(id), (r) => (r ? { ...r, ...(patch.status ? { status: patch.status } : {}), ...(patch.title ? { title: patch.title } : {}) } : r));
}

/* ───────────── 변경 ───────────── */

export type CreateTaskBody = {
  title: string;
  description?: string;
  channelId?: number;
  listId?: number | null;
  parentId?: number;
  assigneeId?: number | null;
  priority?: number;
  dueDate?: string | null;
  dueTime?: string | null;
  rrule?: string | null;
  repeatFromCompletion?: boolean;
  labelIds?: number[];
  status?: TaskDTO["status"];
  silent?: boolean;
  sourceMessageId?: number;
  wikiPageId?: number;
};

export type UpdateTaskBody = Partial<Omit<CreateTaskBody, "parentId" | "sourceMessageId" | "wikiPageId" | "description">> & { description?: string; position?: number };

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTaskBody) => http.post<TaskDTO>("/api/tasks", body),
    onSuccess: (t) => upsertTask(qc, t),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateTaskBody }) => http.patch<TaskDTO>(`/api/tasks/${id}`, body),
    onMutate: ({ id, body }) => {
      // 반복 할일의 완료는 서버가 다음 회차를 정하므로 미리 바꾸지 않는다
      const local: Partial<TaskDTO> = {};
      if (body.status !== undefined) local.status = body.status;
      if (body.position !== undefined) local.position = body.position;
      if (body.title !== undefined) local.title = body.title;
      if (body.priority !== undefined) local.priority = body.priority;
      if (body.assigneeId !== undefined) local.assigneeId = body.assigneeId;
      if (Object.keys(local).length) patchTaskLocal(qc, id, local);
    },
    onSuccess: (t) => upsertTask(qc, t),
    onError: () => {
      void qc.invalidateQueries({ queryKey: ["board"] });
      void qc.invalidateQueries({ queryKey: ["tasks"] });
      void qc.invalidateQueries({ queryKey: ["task"] });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (t: TaskDTO) => http.delete(`/api/tasks/${t.id}`),
    onSuccess: (_r, t) => removeTask(qc, t.id, t.parentId),
  });
}

export function useRestoreTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => http.post<TaskDTO>(`/api/tasks/${id}/restore`),
    onSuccess: (t) => {
      upsertTask(qc, t);
      void qc.invalidateQueries({ queryKey: taskKeys.task(t.id) });
    },
  });
}

export function useSetReminders(taskId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reminders, bypassQuiet }: { reminders: ReminderInput[]; bypassQuiet: boolean }) => http.put<ReminderDTO[]>(`/api/tasks/${taskId}/reminders`, { reminders, bypassQuiet }),
    onSuccess: (reminders) => qc.setQueryData<TaskDetailDTO>(taskKeys.task(taskId), (d) => (d ? { ...d, reminders } : d)),
  });
}

export function useReminderAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, action }: { taskId: number; action: "complete" | "snooze" | "tomorrow" }) => http.post<TaskDTO>(`/api/tasks/${taskId}/reminder-action`, { action }),
    onSuccess: (t) => {
      upsertTask(qc, t);
      void qc.invalidateQueries({ queryKey: taskKeys.task(t.id) });
    },
  });
}

export function useQuietHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (quietHours: { start: string; end: string } | null) => http.put<MeDTO>("/api/me/quiet-hours", { quietHours }),
    onSuccess: (me) => qc.setQueryData<BootstrapDTO>(baseKeys.bootstrap, (b) => (b ? { ...b, me } : b)),
  });
}

export function useDefaultReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (minutes: number | null) => http.put<MeDTO>("/api/me/default-reminder", { minutes }),
    onSuccess: (me) => qc.setQueryData<BootstrapDTO>(baseKeys.bootstrap, (b) => (b ? { ...b, me } : b)),
  });
}

export function useLabelMutations() {
  const qc = useQueryClient();
  const done = () => {
    void qc.invalidateQueries({ queryKey: taskKeys.labels });
    void qc.invalidateQueries({ queryKey: taskKeys.labelEvents });
  };
  return {
    create: useMutation({ mutationFn: (body: { name: string; scope: LabelDTO["scope"]; color?: string }) => http.post<LabelDTO>("/api/labels", body), onSuccess: done }),
    update: useMutation({ mutationFn: ({ id, ...body }: { id: number; name?: string; color?: string }) => http.patch<LabelDTO>(`/api/labels/${id}`, body), onSuccess: done }),
    remove: useMutation({ mutationFn: (id: number) => http.delete(`/api/labels/${id}`), onSuccess: done }),
  };
}

export function useListMutations() {
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: taskKeys.lists });
  return {
    // 만든 목록으로 바로 이동하므로 캐시에 먼저 넣는다 (없으면 목록 화면이 받은함으로 돌려보낸다)
    create: useMutation({
      mutationFn: (name: string) => http.post<PersonalListDTO>("/api/lists", { name }),
      onSuccess: (l) => {
        qc.setQueryData<PersonalListDTO[]>(taskKeys.lists, (list) => (list && !list.some((x) => x.id === l.id) ? [...list, l] : list));
        done();
      },
    }),
    update: useMutation({ mutationFn: ({ id, ...body }: { id: number; name?: string; position?: number }) => http.patch(`/api/lists/${id}`, body), onSuccess: done }),
    remove: useMutation({
      mutationFn: (id: number) => http.delete(`/api/lists/${id}`),
      onSuccess: () => {
        done();
        void qc.invalidateQueries({ queryKey: ["tasks"] });
      },
    }),
  };
}

export function useFilterMutations() {
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: taskKeys.filters });
  return {
    create: useMutation({
      mutationFn: (body: { name: string; conditions: TaskFilter }) => http.post<SavedFilterDTO>("/api/filters", body),
      onSuccess: (f) => {
        qc.setQueryData<SavedFilterDTO[]>(taskKeys.filters, (list) => (list && !list.some((x) => x.id === f.id) ? [...list, f] : list));
        done();
      },
    }),
    update: useMutation({ mutationFn: ({ id, ...body }: { id: number; name?: string; conditions?: TaskFilter }) => http.patch<SavedFilterDTO>(`/api/filters/${id}`, body), onSuccess: done }),
    remove: useMutation({ mutationFn: (id: number) => http.delete(`/api/filters/${id}`), onSuccess: done }),
  };
}

export type DoneData = InfiniteData<DoneHistoryPage, number>;
